import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Maximize2, Minimize2, Loader, AlertTriangle } from 'lucide-react';
import { generateResponse } from '../api/ollama';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  actions?: ActionButton[];
}

interface SpaceChatProps {
  buildingData: {
    cleaning: {
      todayTasks: {
        id: string;
        area: string;
        utilization: number;
        utilizationThreshold: number;
      }[];
    };
    occupancy: {
      currentTotal: number;
      capacity: number;
      peakHours: string;
      byZone: ZoneData[];
      behavioralInsights: {
        type: string;
        value: string;
        trend: string;
        impact: 'high' | 'medium' | 'low';
      }[];
    };
    spaceEfficiency: {
      overall: number;
      categories: {
        pathEfficiency: number;
        spaceUtilization: number;
        collaboration: number;
        roomFit: number;
      };
      recommendations: {
        category: string;
        score: number;
        impact: 'high' | 'medium' | 'low';
        suggestion: string;
      }[];
    };
    pathAnalytics: {
      averagePathLength: string;
      averageDuration: string;
      commonPatterns: string[];
      peakInteractionAreas: {
        area: string;
        interactions: number;
      }[];
    };
    badgeAnalytics: {
      today: {
        totalScans: number;
        activeUsers: number;
        shortVisits: number;
        avgDuration: string;
        peakEntry: string;
        unusualPatterns: {
          time: string;
          event: string;
          count: number;
        }[];
      };
    };
    rooms: {
      all: Room[];
    };
    environment: {
      temperature: number;
      humidity: number;
      co2: number;
      noise: number;
    };
    flooringRecommendations: {
      zones: {
        zone: string;
        recommendedFlooring: {
          product: string;
          features: string[];
        };
        footTraffic: number;
        risk: string;
      }[];
      generalInfo: {
        website: string;
        specialties: string[];
        certifications: string[];
      };
    };
    healthcare: {
      residents: {
        details: Resident[];
      };
      staffing: {
        shifts: Shift[];
      };
    };
  };
  onBookRoom?: (roomId: string, timeSlot: string) => void;
  onReserveDesk?: (deskId: string, timeSlot: string) => void;
  onCreateCleaningTicket?: (area: string, reason: string) => void;
  onCreateMaintenanceTicket?: (system: string, issue: string) => void;
  onCreateSupplyTicket?: (item: string, currentLevel: number) => void;
}

interface ZoneData {
  zone: string;
  count: number;
  capacity: number;
}

interface TaskData {
  id: string;
  area: string;
  utilization: number;
  utilizationThreshold: number;
}

interface InventoryItem {
  item: string;
  current: number;
  threshold: number;
}

interface ActionButton {
  type: 'CLEANING' | 'MAINTENANCE' | 'SUPPLY';
  area?: string;
  reason?: string;
  system?: string;
  issue?: string;
  item?: string;
  level?: number;
}

interface Room {
  name: string;
  status: 'available' | 'occupied' | 'booked';
  sensorData: {
    activePaths: number;
  };
}

interface Resident {
  name: string;
  room: string;
  status: string;
  alerts: Alert[];
  vitals: {
    heartRate: number;
    bloodPressure: string;
    temperature: number;
  };
}

interface Alert {
  message: string;
}

interface Shift {
  shift: string;
  staff: Staff[];
}

interface Staff {
  name: string;
  role: string;
}

const SpaceChat: React.FC<SpaceChatProps> = ({ 
  buildingData, 
  onBookRoom, 
  onReserveDesk,
  onCreateCleaningTicket,
  onCreateMaintenanceTicket,
  onCreateSupplyTicket
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: "Hello! I'm your space assistant. Ask me anything about the building, resources, or current conditions.",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateSystemContext = () => {
    const availableRooms = buildingData.rooms.all.filter((r: Room) => r.status === 'available');
    const occupiedRooms = buildingData.rooms.all.filter((r: Room) => r.status === 'occupied');
    const bookedRooms = buildingData.rooms.all.filter((r: Room) => r.status === 'booked');
    
    const formatRoomStatus = (room: Room) => 
      `${room.name} (${room.status}${room.sensorData.activePaths ? `, ${room.sensorData.activePaths} active paths` : ''})`;

    // Add healthcare context
    const urgentResidents = buildingData.healthcare.residents.details.filter(r => r.status === 'urgent');
    const currentShift = (() => {
      const hour = new Date().getHours();
      if (hour >= 7 && hour < 15) return 'morning';
      if (hour >= 15 && hour < 23) return 'afternoon';
      return 'night';
    })();
    const currentShiftStaff = buildingData.healthcare.staffing.shifts.find(s => s.shift === currentShift);

    return `You are an AI assistant for a senior living facility equipped with advanced monitoring systems for both the building and resident care. You have access to real-time data about residents, staff, and building conditions.

Healthcare Status:
- Total Residents: ${buildingData.healthcare.residents.details.length}
- Urgent Cases: ${urgentResidents.length}
- Current Shift: ${currentShift}
- Staff on Duty: ${currentShiftStaff?.staff.map(s => `${s.name} (${s.role})`).join(', ')}

${buildingData.healthcare.residents.details.some(r => r.status === 'urgent') ? 
`Residents Needing Immediate Attention:
${urgentResidents.map(r => `- ${r.name} in Room ${r.room}${r.alerts.length ? ` (${r.alerts.length} active alerts)` : ''}`).join('\n')}` : 
'No residents currently require immediate attention.'}

Building Status:
- Total occupancy: ${buildingData.occupancy.currentTotal}/${buildingData.occupancy.capacity}
- Peak hours: ${buildingData.occupancy.peakHours}
- Zone occupancy: ${buildingData.occupancy.byZone.map((z: ZoneData) => `${z.zone}: ${z.count}/${z.capacity}`).join(', ')}

Flooring Solutions by Mannington Commercial:
${buildingData.flooringRecommendations.zones.map(zone => 
  `- ${zone.zone}: ${zone.recommendedFlooring.product}
   • Traffic Level: ${zone.footTraffic} paths/day
   • Risk Level: ${zone.risk}
   • Features: ${zone.recommendedFlooring.features.join(', ')}`
).join('\n')}

Mannington Commercial Information:
- Website: ${buildingData.flooringRecommendations.generalInfo.website}
- Specialties: ${buildingData.flooringRecommendations.generalInfo.specialties.join(', ')}
- Key Technologies: ${buildingData.flooringRecommendations.generalInfo.certifications.join(', ')}

Space Efficiency Score:
- Overall Score: ${buildingData.spaceEfficiency.overall}/100
- Path Efficiency: ${buildingData.spaceEfficiency.categories.pathEfficiency}/100
- Space Utilization: ${buildingData.spaceEfficiency.categories.spaceUtilization}/100
- Collaboration Score: ${buildingData.spaceEfficiency.categories.collaboration}/100
- Room Fit Score: ${buildingData.spaceEfficiency.categories.roomFit}/100

Path Analytics:
- Average Path Length: ${buildingData.pathAnalytics.averagePathLength}
- Average Duration: ${buildingData.pathAnalytics.averageDuration}
- Common Patterns: ${buildingData.pathAnalytics.commonPatterns.join(', ')}
- Peak Interaction Areas: ${buildingData.pathAnalytics.peakInteractionAreas.map(area => `${area.area} (${area.interactions} interactions)`).join(', ')}

Badge Analytics:
- Total Scans Today: ${buildingData.badgeAnalytics.today.totalScans}
- Active Users: ${buildingData.badgeAnalytics.today.activeUsers}
- Short Visits: ${buildingData.badgeAnalytics.today.shortVisits}
- Peak Entry Time: ${buildingData.badgeAnalytics.today.peakEntry}

Behavioral Insights:
${buildingData.occupancy.behavioralInsights.map(insight => 
  `- ${insight.type}: ${insight.value} (${insight.trend}, ${insight.impact} impact)`
).join('\n')}

For room booking requests:
1. Check if the requested room is available for the specified time
2. If available, respond with: ACTION: BOOK_ROOM|[room_id]|[start_time]-[end_time]
3. If not available:
   - Check sensor data for potentially available rooms (booked but no activity)
   - Suggest alternative available rooms
4. Keep responses concise and clear

When asked about flooring recommendations:
1. Consider the zone's traffic patterns and risk level
2. Recommend appropriate Mannington Commercial products
3. Highlight relevant features and benefits
4. Reference specific product lines based on use case

Response Format Examples:
1. Room Available: "ACTION: BOOK_ROOM|CONF_A|13:00-14:00\nI've booked Boardroom A for you from 1 PM to 2 PM."
2. Room Unavailable: "Boardroom A is currently occupied. Focus Room 1 is available and can accommodate your group. Would you like me to book that instead?"
3. Flooring Recommendation: "For high-traffic areas like the main corridor, I recommend Mannington Quantum Guard® HP - BioSpec® RE, which offers superior durability and infection control features."

Keep all responses focused, direct, and limited to 1-2 sentences.`;
  };

  const generateLLMPrompt = (userQuery: string) => {
    return `You are a concise AI assistant for a smart building with floor sensors and monitoring systems. 
    Keep responses brief and actionable, ideally 1-2 sentences.
    
    Current building status:
    ${generateSystemContext()}
    
    User query: ${userQuery}
    
    IMPORTANT: For room booking requests, you MUST ALWAYS respond in this EXACT format:
    1. First line: ACTION: BOOK_ROOM|[room_id]|[start_time]-[end_time]
    2. Second line: A brief confirmation message
    
    Example of CORRECT booking response:
    ACTION: BOOK_ROOM|CONF_A|13:00-14:00
    I've booked Conference Room A for you from 1 PM to 2 PM.

    Example of INCORRECT booking response:
    I've booked Conference Room A for you from 1 PM to 2 PM.
    
    Never confirm a booking without first including the ACTION command.
    For rooms that are available, always include both the action command AND the confirmation message.
    For unavailable rooms, suggest alternatives but do not include an action command.

    Keep all responses focused and direct. If information isn't available, say so briefly.`;
  };

  const handleAction = (actionString: string): ActionButton | void => {
    const [action, resource, details] = actionString.split('|');
    
    switch (action) {
      case 'BOOK_ROOM':
        if (onBookRoom) {
          onBookRoom(resource, details);
        }
        break;
      case 'RESERVE_DESK':
        if (onReserveDesk) {
          onReserveDesk(resource, details);
        }
        break;
      case 'CREATE_CLEANING_TICKET':
        return {
          type: 'CLEANING' as const,
          area: resource,
          reason: details
        };
      case 'CREATE_MAINTENANCE_TICKET':
        return {
          type: 'MAINTENANCE' as const,
          system: resource,
          issue: details
        };
      case 'CREATE_SUPPLY_TICKET':
        return {
          type: 'SUPPLY' as const,
          item: resource,
          level: parseInt(details)
        };
    }
  };

  const callLLMAPI = async (prompt: string): Promise<string> => {
    try {
      const response = await generateResponse(prompt);
      return response;
    } catch (error) {
      console.error('Error calling Ollama:', error);
      return "I apologize, but I'm having trouble connecting to the local AI service. Please ensure Ollama is running and try again.";
    }
  };

  const processHealthcareQuery = (query: string): string | undefined => {
    const healthcare = buildingData.healthcare;
    
    // Handle resident status queries
    if (query.toLowerCase().includes('need immediate attention') || 
        query.toLowerCase().includes('urgent status')) {
      const urgentResidents = healthcare.residents.details.filter(r => r.status === 'urgent');
      if (urgentResidents.length === 0) {
        return 'There are currently no residents requiring immediate attention.';
      }
      
      const residentList = urgentResidents.map(r => {
        const alerts = r.alerts.length > 0 
          ? ` (${r.alerts.length} active alerts: ${r.alerts.map(a => a.message).join(', ')})`
          : '';
        return `- ${r.name} in Room ${r.room}${alerts}`;
      }).join('\n');
      
      return `${urgentResidents.length} residents need immediate attention:\n${residentList}`;
    }

    // Handle staffing queries
    if (query.toLowerCase().includes('working') && query.toLowerCase().includes('shift')) {
      const shift = query.toLowerCase().includes('morning') ? 'morning' :
                   query.toLowerCase().includes('afternoon') ? 'afternoon' :
                   query.toLowerCase().includes('night') ? 'night' : null;
                   
      if (shift) {
        const shiftData = healthcare.staffing.shifts.find(s => s.shift === shift);
        if (shiftData) {
          const staffList = shiftData.staff.map(s => 
            `- ${s.name} (${s.role})`
          ).join('\n');
          return `Staff working the ${shift} shift:\n${staffList}`;
        }
      }
    }

    // Handle alert queries
    if (query.toLowerCase().includes('active alerts')) {
      const residentsWithAlerts = healthcare.residents.details.filter(r => r.alerts.length > 0);
      if (residentsWithAlerts.length === 0) {
        return 'There are currently no active alerts.';
      }
      
      const alertsList = residentsWithAlerts.map(r => 
        `- ${r.name} (Room ${r.room}): ${r.alerts.map(a => a.message).join(', ')}`
      ).join('\n');
      
      return `Active alerts for ${residentsWithAlerts.length} residents:\n${alertsList}`;
    }

    // Handle vital signs queries
    if (query.toLowerCase().includes('vital sign')) {
      const recentVitals = healthcare.residents.details.map(r => ({
        name: r.name,
        room: r.room,
        vitals: r.vitals
      }));
      
      const vitalsList = recentVitals.map(r => 
        `- ${r.name} (Room ${r.room}): HR: ${r.vitals.heartRate}bpm, BP: ${r.vitals.bloodPressure}, Temp: ${r.vitals.temperature.toFixed(1)}°F`
      ).join('\n');
      
      return `Recent vital signs:\n${vitalsList}`;
    }

    return undefined;
  };

  const processMessage = async (message: string) => {
    // First try healthcare-specific responses
    const healthcareResponse = processHealthcareQuery(message);
    if (healthcareResponse) {
      return healthcareResponse;
    }

    // If no healthcare response, generate LLM response
    const prompt = generateLLMPrompt(message);
    return await callLLMAPI(prompt);
  };

  const handleSend = async (message: string) => {
    if (!message.trim()) return;
    
    try {
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: message,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);
      setInputValue('');
      setIsLoading(true);

      const response = await processMessage(message);

      // Check if response includes ACTION command
      const actionMatches = response.match(/ACTION: ([^\n]+)/g);
      const actions: ActionButton[] = [];
      
      if (actionMatches) {
        actionMatches.forEach(match => {
          const actionString = match.replace('ACTION: ', '');
          const action = handleAction(actionString);
          if (action) {
            actions.push(action);
          }
        });
      }

      // Clean response by removing ACTION lines
      const cleanResponse = response.replace(/ACTION: [^\n]+\n/g, '').trim();
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: cleanResponse,
        timestamp: new Date(),
        actions: actions.length > 0 ? actions : undefined
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error generating response:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'I apologize, but I encountered an error connecting to the local AI service. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(inputValue);
    }
  };

  const handleActionButtonClick = (action: ActionButton) => {
    switch (action.type) {
      case 'CLEANING':
        if (onCreateCleaningTicket && action.area && action.reason) {
          onCreateCleaningTicket(action.area, action.reason);
        }
        break;
      case 'MAINTENANCE':
        if (onCreateMaintenanceTicket && action.system && action.issue) {
          onCreateMaintenanceTicket(action.system, action.issue);
        }
        break;
      case 'SUPPLY':
        if (onCreateSupplyTicket && action.item && action.level) {
          onCreateSupplyTicket(action.item, action.level);
        }
        break;
    }
  };

  return (
    <div className={`fixed bottom-6 right-6 z-50 ${isOpen ? 'flex' : ''}`}>
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-4 shadow-lg flex items-center justify-center transition-all duration-200"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      ) : (
        <div
          ref={chatContainerRef}
          className={`bg-white rounded-lg shadow-xl flex flex-col ${
            isExpanded ? 'fixed top-4 right-4 left-4 bottom-4' : 'w-96 h-[600px]'
          }`}
        >
          {/* Chat Header */}
          <div className="p-4 border-b flex items-center justify-between bg-blue-500 text-white rounded-t-lg">
            <h3 className="font-semibold">Space Assistant</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1 hover:bg-blue-600 rounded"
                title={isExpanded ? "Shrink" : "Expand"}
              >
                {isExpanded ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-blue-600 rounded"
                title="Minimize"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setMessages([{
                    id: '1',
                    role: 'assistant',
                    content: "Hello! I'm your space assistant. Ask me anything about the building, resources, or current conditions.",
                    timestamp: new Date()
                  }]);
                }}
                className="p-1 hover:bg-blue-600 rounded"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <p className="text-sm">{message.content}</p>
                  {message.actions && message.actions.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {message.actions.map((action, index) => (
                        <button
                          key={index}
                          onClick={() => handleActionButtonClick(action)}
                          className="w-full text-sm bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors"
                        >
                          {action.type === 'CLEANING' && `Create Cleaning Ticket: ${action.area}`}
                          {action.type === 'MAINTENANCE' && `Create Maintenance Ticket: ${action.system}`}
                          {action.type === 'SUPPLY' && `Create Supply Request: ${action.item}`}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="text-xs mt-1 opacity-70">
                    {message.timestamp.toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg p-3">
                  <Loader className="w-5 h-5 animate-spin text-blue-500" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about your space..."
                className="flex-1 border rounded-lg px-4 py-2 focus:outline-none focus:border-blue-500"
                disabled={isLoading}
              />
              <button
                onClick={() => handleSend(inputValue)}
                disabled={!inputValue.trim() || isLoading}
                className="bg-blue-500 text-white rounded-lg p-2 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpaceChat;